import type { FeatureRestriction } from '@/lib/feature-permissions';

export type AccessRestriction = Extract<FeatureRestriction, 'login' | 'upgrade'>;

export interface UserFeatures {
  kline_chart: boolean;
  trading_prefs: boolean;
  market_research: boolean;
  live_trading_data: boolean;
  ai_deep_review: boolean;
}

export interface UserAccess {
  session_limit: number;
  can_manage_sessions: boolean;
  features: UserFeatures;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function normalizeSessionLimit(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 1;
}

export function normalizeUserAccess(value: unknown): UserAccess {
  const source = asRecord(value);
  const features = asRecord(source.features);

  return {
    session_limit: normalizeSessionLimit(source.session_limit),
    can_manage_sessions: source.can_manage_sessions === true,
    features: {
      kline_chart: features.kline_chart === true,
      trading_prefs: features.trading_prefs === true,
      market_research: features.market_research === true,
      live_trading_data: features.live_trading_data === true,
      ai_deep_review: features.ai_deep_review === true,
    },
  };
}

export function isUnlimitedSessionLimit(limit: number): boolean {
  return limit === 0;
}

export function hasReachedSessionLimit(access: unknown, sessionCount: number): boolean {
  const limit = normalizeUserAccess(access).session_limit;
  return !isUnlimitedSessionLimit(limit) && sessionCount >= limit;
}

export function getFeatureRestriction(enabled: boolean, isLoggedIn: boolean): AccessRestriction | null {
  if (enabled) return null;
  return isLoggedIn ? 'upgrade' : 'login';
}
